import {
  Stack,
  Text,
  Button,
  Box,
  Radio,
  RadioGroup,
  VStack,
  HStack,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { NestedViewing, NestedHome } from '@habity/types';
import {
  formatNextViewing,
  isViewingSlotFullyBooked,
  getHomePath,
} from '@habity/services';
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useUser } from '~/hooks/useUser';
import { TwoStepEnhancedLoginForm } from '~/components/auth/components/TwoStepEnhancedLoginForm';
import { useUIStore } from '~/store/useUIStore';
import { capitalizeFirstLetter } from '@habity/utils';

interface ViewingSlotSelectorProps {
  viewing: NestedViewing;
  home: NestedHome;
  selectedSlotId: string;
  setSelectedSlotId: (slotId: string) => void;
  onSlotSelect: (slotId: number) => void;
  onBack: () => void;
  isLoading?: boolean;
  requiresAuth?: boolean;
  onLoginFormChange?: (showing: boolean) => void;
}

export const ViewingSlotSelector = ({
  viewing,
  home,
  selectedSlotId,
  setSelectedSlotId,
  onSlotSelect,
  onBack,
  isLoading = false,
  requiresAuth = false,
  onLoginFormChange,
}: ViewingSlotSelectorProps) => {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const { user } = useUser();

  // Notify parent when login form state changes
  useEffect(() => {
    onLoginFormChange?.(showLoginForm);
  }, [showLoginForm, onLoginFormChange]);

  // Auto-select first slot
  useEffect(() => {
    if (viewing.viewing_slots && viewing.viewing_slots.length > 0) {
      const firstSlotId = viewing.viewing_slots[0].id;
      setSelectedSlotId(firstSlotId.toString());

      // For single slot with unauthenticated users, also show login form
      if (viewing.viewing_slots.length === 1 && requiresAuth && !user) {
        const { setLoginViaViewingSlotId } = useUIStore.getState();
        setLoginViaViewingSlotId(firstSlotId);
        setShowLoginForm(true);
      }
    }
  }, [requiresAuth, user, viewing.viewing_slots]);

  const handleConfirm = () => {
    if (!selectedSlotId) return;

    const slotIdNumber = parseInt(selectedSlotId);

    // If user is not logged in and auth is required, store slot ID and show login form
    if (requiresAuth && !user) {
      // Store the viewing slot ID BEFORE showing login form
      // so it's available when the form submits the booking
      const { setLoginViaViewingSlotId } = useUIStore.getState();
      setLoginViaViewingSlotId(slotIdNumber);
      setShowLoginForm(true);
      return;
    }

    onSlotSelect(slotIdNumber);
  };

  const handleLoginSuccess = async () => {
    // After successful login, the booking has already been made by TwoStepEnhancedLoginForm
    // The form will show its own confirmation screen, so we don't need to do anything here
    // The modal will stay open to show the confirmation, and user can close it manually
  };

  const formatSlotTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    return `${format(start, 'HH:mm', { locale: sv })} - ${format(end, 'HH:mm', {
      locale: sv,
    })}`;
  };

  const getAvailableSeats = (
    slot: NonNullable<NestedViewing['viewing_slots']>[0]
  ) => {
    // If participants_per_slot is null, it means unlimited seats
    if (
      viewing.participants_per_slot === null ||
      viewing.participants_per_slot === undefined
    ) {
      return null; // null indicates unlimited
    }
    const maxSeats = viewing.participants_per_slot;
    const takenSeats = slot.viewing_participants?.length || 0;
    return Math.max(0, maxSeats - takenSeats);
  };

  if (!viewing.viewing_slots || viewing.viewing_slots.length === 0) {
    // Fallback to original booking if no slots
    return (
      <Stack spacing="4">
        <Text fontWeight="medium" size="lg">
          Boka visning
        </Text>
        <Text size="md">{formatNextViewing(viewing, true)}</Text>
        <HStack spacing="3">
          <Button variant="outline" onClick={onBack}>
            Tillbaka
          </Button>
          <Button
            variant="primary"
            onClick={() => onSlotSelect(0)} // 0 indicates no specific slot
            isLoading={isLoading}
          >
            Boka visning
          </Button>
        </HStack>
      </Stack>
    );
  }

  // Show login form if user needs to authenticate
  if (showLoginForm && !user) {
    const selectedSlot =
      selectedSlotId &&
      viewing.viewing_slots?.find((s) => s.id.toString() === selectedSlotId);
    const formattedDate = selectedSlot
      ? capitalizeFirstLetter(
          format(new Date(selectedSlot.start_time), 'EEEE d MMMM', {
            locale: sv,
          })
        )
      : '';
    const formattedTime = selectedSlot
      ? formatSlotTime(selectedSlot.start_time, selectedSlot.end_time)
      : '';

    return (
      <Stack spacing="4">
        <Box>
          <Text fontWeight="medium" size="lg" mb="2">
            {formattedDate}
          </Text>
          <Text size="md" color="gray.600" mb="4">
            Vald tid: {formattedTime}
          </Text>
        </Box>

        <TwoStepEnhancedLoginForm
          authHome={home}
          authProject={null}
          authAction="booked_viewing"
          redirectTo={getHomePath(home)}
          onSuccess={handleLoginSuccess}
          hideHeader={true}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing="4">
      <Box>
        {viewing.viewing_slots && viewing.viewing_slots.length > 0 && (
          <Text fontWeight="medium" size="lg" mb="2">
            {capitalizeFirstLetter(
              format(
                new Date(viewing.viewing_slots[0].start_time),
                'EEEE d MMMM',
                {
                  locale: sv,
                }
              )
            )}
          </Text>
        )}

        {viewing.description && (
          <Text size="md" color="gray.600">
            {viewing.description}
          </Text>
        )}
      </Box>

      <RadioGroup value={selectedSlotId} onChange={setSelectedSlotId}>
        <VStack align="stretch" spacing="3">
          {viewing.viewing_slots
            .sort(
              (a, b) =>
                new Date(a.start_time).getTime() -
                new Date(b.start_time).getTime()
            )
            .map((slot) => {
              const availableSeats = getAvailableSeats(slot);
              const isFull = isViewingSlotFullyBooked(
                slot,
                viewing.participants_per_slot
              );

              return (
                <Box
                  key={slot.id}
                  p="3"
                  border="1px solid"
                  borderColor={
                    selectedSlotId === slot.id.toString()
                      ? 'blue.200'
                      : 'gray.200'
                  }
                  borderRadius="md"
                  bg={
                    isFull
                      ? 'gray.50'
                      : selectedSlotId === slot.id.toString()
                      ? 'blue.50'
                      : 'white'
                  }
                  opacity={isFull ? 0.6 : 1}
                  cursor={isFull ? 'not-allowed' : 'pointer'}
                  onClick={() =>
                    !isFull && setSelectedSlotId(slot.id.toString())
                  }
                >
                  <HStack justify="space-between" align="center">
                    <HStack spacing="3">
                      <Radio
                        value={slot.id.toString()}
                        isDisabled={isFull}
                        colorScheme="blue"
                      />
                      <VStack align="start" spacing="1">
                        <Text fontWeight="medium" size="md">
                          {formatSlotTime(slot.start_time, slot.end_time)}
                        </Text>
                        {/* <Text size="sm" color="gray.600">
                        {format(new Date(slot.start_time), 'EEEE d MMMM', {
                          locale: sv,
                        })}
                      </Text> */}
                      </VStack>
                    </HStack>

                    <VStack align="end" spacing="1">
                      {isFull ? (
                        <Badge colorScheme="red" size="sm">
                          Fullbokad
                        </Badge>
                      ) : availableSeats !== null &&
                        availableSeats < 3 &&
                        viewing.participants_per_slot !== null &&
                        viewing.participants_per_slot > availableSeats ? (
                        <Badge colorScheme="orange" size="sm">
                          {availableSeats}{' '}
                          {availableSeats === 1 ? 'plats' : 'platser'} kvar
                        </Badge>
                      ) : null}
                    </VStack>
                  </HStack>
                </Box>
              );
            })}
        </VStack>
      </RadioGroup>

      <Button
        variant="primary"
        onClick={handleConfirm}
        isDisabled={!selectedSlotId}
        isLoading={isLoading}
        size="lg"
        w="full"
      >
        Boka vald tid
      </Button>
    </Stack>
  );
};
